/* eslint-disable max-lines */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { Observable, of, throwError, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  repeatWhen,
  retryWhen,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { selectBffBaseUrl } from '../store/selectors/api-config.selectors';
import {
  ActivationCodeResponse,
  ActivityInstance,
  DeviceVerification,
  ProcessDefinitionResponse,
  ProcessInstance,
  ProcessInstanceResponse,
  ProcessInstancesResponse,
  SecuredDevicesResponse,
} from './two-factor-auth-types';
import { userCategorySelector } from '../store/selectors/user-role.selectors';
import { NativeClientService } from '@shared/services/native-client.service';

export type ProcessType = 'mfa_setup' | 'recovery' | 'onboarding';

@Injectable({
  providedIn: 'root',
})
export class TwoFactorAuthService {
  baseAppAccountPath = '';
  baseAppPath = '';
  processDefinitions$: Observable<ProcessDefinitionResponse>;

  /** @todo  move methods with http requests to api services*/
  constructor(
    private http: HttpClient,
    private readonly nativeClient: NativeClientService,
    private sanitizer: DomSanitizer,
    private store: Store,
  ) {
    this.store.pipe(selectBffBaseUrl).subscribe((url) => {
      this.baseAppAccountPath = `${url}/app/account`;
      /** @todo move to app-config */
      this.baseAppPath = `${url}/app`;
    });
  }

  getDevices(): Observable<DeviceVerification[]> {
    return this.http.get<SecuredDevicesResponse>(
      `${this.baseAppAccountPath}/myaccount/secureDevice`,
    );
  }

  getVerifiedDevices(): Observable<DeviceVerification[]> {
    return this.getDevices().pipe(
      map((verifications) => {
        return verifications.filter(({ status }) => status === 'VERIFIED');
      }),
    );
  }

  getVerificationStatus() {
    return this.http
      .get(`${this.baseAppAccountPath}/myaccount/identity`)
      .pipe(map((status) => status));
  }

  hasVerifiedDevice(): Observable<boolean> {
    return this.getVerifiedDevices().pipe(map((devices) => !!devices.length));
  }

  private getMultiFactorProcessDefinitions({
    type,
  }: { type?: ProcessType } = {}) {
    this.processDefinitions$ =
      this.processDefinitions$ ||
      this.http
        .get<ProcessDefinitionResponse>(
          `${this.baseAppAccountPath}/myaccount/process/definition`,
          {
            headers: {
              'Content-Type': 'text/plain',
            },
          },
        )
        .pipe(shareReplay(1));

    return this.processDefinitions$.pipe(
      map((definitions) => {
        return type
          ? {
              ...definitions,
              processDefinitions: definitions.processDefinitions.filter(
                (processDefinition) => processDefinition.type === type,
              ),
            }
          : definitions;
      }),
      tap((definitions) => {
        if (!definitions?.processDefinitions?.[0]?.type) {
          throw new Error(
            `No multi factor process definiations available for type '${type}'`,
          );
        }
      }),
    );
  }

  private getProcessDefinitionName({
    type,
  }: {
    type: ProcessType;
  }): Observable<string> {
    return this.getMultiFactorProcessDefinitions({ type }).pipe(
      map((definitions) => {
        return definitions?.processDefinitions?.[0]?.name;
      }),
    );
  }

  private getActiveProcessInstances(
    type?: ProcessType,
  ): Observable<ProcessInstancesResponse> {
    return this.http.get<ProcessInstancesResponse>(
      `${this.baseAppAccountPath}/myaccount/process/instance` +
        (type ? `?type=${type}` : ''),
    );
  }

  public pollRole(role: any) {
    if (this.nativeClient.isNative) {
      this.nativeClient.fetchNewToken();
    }
    return this.http.get(`${this.baseAppPath}/user/token/has-role/${role}`, {
      observe: 'response',
    });
  }

  public startNewProcessInstance(
    { processDefinition },
    userCategory?: string,
  ): Observable<ProcessInstance> {
    return this.store.pipe(
      select(userCategorySelector),
      switchMap((category) => {
        return this.http
          .post<ProcessInstanceResponse>(
            `${this.baseAppAccountPath}/myaccount/process/instance/$newInstance`,
            {
              processDefinition,
              userCategory: userCategory ? userCategory : category,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          )
          .pipe(map(({ processInstance }) => processInstance));
      }),
    );
  }

  startProcessByType({ type }: { type: ProcessType }) {
    return this.getCurrentProcessInstance({ type }).pipe(
      switchMap(
        (processInstance?: ProcessInstance): Observable<ProcessInstance> => {
          return processInstance
            ? of(processInstance)
            : this.getProcessDefinitionName({
                type,
              }).pipe(
                switchMap((name) =>
                  this.startNewProcessInstance({
                    processDefinition: name,
                  }),
                ),
              );
        },
      ),
    );
  }

  private getProcessById(id: string): Observable<ProcessInstance> {
    return this.http
      .get<ProcessInstanceResponse>(
        `${this.baseAppAccountPath}/myaccount/process/instance/${id}`,
      )
      .pipe(map(({ processInstance }) => processInstance));
  }

  getCurrentProcessInstance({
    type,
  }: {
    type?: ProcessType;
  } = {}): Observable<ProcessInstance | null> {
    return this.getActiveProcessInstances(type).pipe(
      map(({ processInstances }) => {
        return processInstances[0];
      }),
      map((process) => process?.processId),
      switchMap((processId) => {
        return processId ? this.getProcessById(processId) : of(null);
      }),
    );
  }

  private getLatestInteractiveActivityOfProcess(
    process: ProcessInstance,
  ): ActivityInstance | null {
    if (process) {
      const currentActivity =
        process.activityInstances[process.activityInstances.length - 1];
      if (currentActivity && currentActivity.interactive) {
        return currentActivity;
      }
    }
    return null;
  }

  private activateCurrentStep({ type }, isRecovery?) {
    return this.waitForInteractiveActivtivityInstance({ type }).pipe(
      switchMap((activityInstance) => {
        if (isRecovery) {
          return this.http.post(
            `${this.baseAppAccountPath}/myaccount/process/activity/instance/${activityInstance.activityId}/$activate`,
            {
              step: 'redeem_recovery_key',
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          );
        } else if (activityInstance.status === 'suspended') {
          return this.http.post(
            `${this.baseAppAccountPath}/myaccount/process/activity/instance/${activityInstance.activityId}/$activate`,
            {
              step: activityInstance.activityDefinition,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          );
        }

        return of(activityInstance);
      }),
    );
  }

  // MFA Setup
  requestActivationForNewDevice({
    type = 'mfa_setup',
  }: {
    type?: ProcessType;
  } = {}) {
    const request$ =
      type === 'recovery'
        ? this.getActivationCode()
        : this.startProcessByType({ type }).pipe(
            // todo this could be simplyfiyed by reusing waitForInteractiveActivtivityInstance
            // .switchMap(() => waitForInteractiveActivtivityInstance({type: 'mfa_setup'}))
            switchMap((process) => {
              if (process.status === 'active') {
                const currentActivity =
                  process.activityInstances[
                    process.activityInstances.length - 1
                  ];
                if (currentActivity && currentActivity.interactive) {
                  return this.getActivationCode();
                }
                throw new Error('No interactive activity'); // triggers retry bleow
              }
              throw new Error('No active process'); // triggers retry below
            }),
          );

    return request$.pipe(
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error, i) => (i < 5 ? timer(1000) : throwError(error))),
        ),
      ),
      map((activationCodeResponse: ActivationCodeResponse) => {
        return {
          uri: activationCodeResponse.authenticationMethod.uri,
          activationCode: activationCodeResponse.activationCode,
        };
      }),
    );
  }

  getActivationCode() {
    return this.http
      .post<ActivationCodeResponse>(
        `${this.baseAppAccountPath}/myaccount/secureDevice/$requestActivationCode`,
        {
          vendor: 'SOFTWARE_TOTP',
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .pipe(
        map((response) => {
          const uri = response.authenticationMethod.uri;

          return {
            ...response,
            authenticationMethod: {
              ...response.authenticationMethod,
              uri: this.sanitizer.bypassSecurityTrustUrl(uri), //NOSONAR this has to be bypassed because otpauth uris would be marked as unsafe
            },
          };
        }),
      );
  }

  addDevice({ alias, activationCode, verificationToken, isRecovery = false }) {
    // this.getVerifiedDevices(), // if we need to support addititional devices

    return this.http
      .post(
        `${this.baseAppAccountPath}/myaccount/secureDevice`,
        {
          flow: isRecovery ? 'RECOVERY' : 'INITIAL_DEVICE', // ADDITITIONAL_DEVICE if we have to support addititional devices
          newDevice: {
            vendor: 'SOFTWARE_TOTP',
            alias: alias,
            activationCode,
            verificationToken,
          },
          // ...(devices.length // readd if we have to support addititional devices.
          //   ? {
          //       approvalDevice: {
          //         refId: devices[0].refId,
          //         verificationToken: verificationToken,
          //       },
          //     }
          //   : null), // todo
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .pipe(
        catchError((error) => {
          const errorMessage =
            error?.error?.validationErrors?.[0]?.errorDescription;
          throw errorMessage ? new Error(errorMessage) : error;
        }),
      );
  }

  waitForActiveProcessInstance() {
    return this.http
      .get<ProcessInstancesResponse>(
        `${this.baseAppAccountPath}/myaccount/process/instance?activeOnly=true`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .pipe(
        TwoFactorAuthService.retry,
        map(({ processInstances }) => processInstances?.[0]),
        filter((processInstances) => processInstances !== null),
        take(1),
      );
  }

  waitForInteractiveActivtivityInstance({
    type,
  }): Observable<ActivityInstance> {
    return this.getCurrentProcessInstance({ type }).pipe(
      switchMap((process) => {
        if (process) {
          return of(process);
        } else {
          return this.startProcessByType({ type });
        }
      }),
      map(this.getLatestInteractiveActivityOfProcess),
      TwoFactorAuthService.retry,
      filter(
        (activity: ActivityInstance) =>
          activity !== null &&
          (activity.status === 'suspended' || activity.status === 'active'),
      ),
      take(1),
    );
  }

  private static retry<T>(source: Observable<T>): Observable<T> {
    return source.pipe(
      repeatWhen((x) =>
        x.pipe(
          mergeMap((error, i) => (i < 5 ? timer(1000) : throwError(error))),
        ),
      ),
    );
  }

  requestRecoveryKey({
    type = 'mfa_setup',
  }: {
    type?: ProcessType;
  } = {}): Observable<string> {
    return this.activateCurrentStep({ type }).pipe(
      switchMap(() => {
        return this.http
          .get<{ recoveryKey: string }>(
            `${this.baseAppAccountPath}/myaccount/recoveryKey`,
          )
          .pipe(map(({ recoveryKey }) => recoveryKey));
      }),
    );
  }

  getRecoveryKey(): Observable<string> {
    return this.http
      .get<{ recoveryKey: string }>(
        `${this.baseAppAccountPath}/myaccount/recoveryKey`,
      )
      .pipe(
        shareReplay(1),
        map(({ recoveryKey }) => recoveryKey),
      );
  }

  acknowledgeRecoveryKey(recoveryKey: string): Observable<any> {
    return this.http.post(
      `${this.baseAppAccountPath}/myaccount/recoveryKey/$acknowledge`,
      {
        recoveryKey,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  // Recovery Process
  startRecovery() {
    return this.startNewProcessInstance({ processDefinition: 'recovery' });
  }

  /**
   * Generates support code
   * @returns
   */
  getSupportCode(): Observable<{ code: string; expiresIn: number }> {
    return this.http.post<{ code: string; expiresIn: number }>(
      `${this.baseAppAccountPath}/myaccount/supportRequest`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }
}
